'use client';

import { useCallback, useMemo, type CSSProperties } from 'react';
import sheet from '@/assets/flags-sprite.webp';
import { SHEET, FLAG_INDEX } from '@/lib/flagSprite';
import { useAuth } from '@/lib/auth';

/**
 * One country flag, cut out of the shared sprite.
 *
 * Every flag on the site comes from a single 143KB image instead of one SVG
 * request each. The language picker was the case that made this necessary: ~100
 * languages with up to three flags apiece meant ~250 requests and ~630KB the
 * moment the dropdown opened, none of it cached (`public/` is served
 * `max-age=0`) and all of it queued through HTTP/1.1's six connections.
 *
 * Still not a flag emoji, for the reason recorded in components/ModeIcons.tsx:
 * Windows ships no flag glyphs and renders the two letters instead, which is
 * exactly the lettering a flag is here to replace.
 *
 * The sprite is imported rather than referenced from `public/`, so Next
 * content-hashes it into /_next/static/media and serves it `immutable` — one
 * request per browser, ever, instead of one per dropdown.
 */

const SHEET_W = SHEET.cols * (SHEET.cellW + SHEET.pad * 2);
const SHEET_H = SHEET.rows * (SHEET.cellH + SHEET.pad * 2);

/**
 * Why percentages, and why they land on the right cell.
 *
 * `background-size` is a percentage of the ELEMENT, so scaling the sheet to
 * `sheetW/cellW` of the element's width makes one cell exactly fill it —
 * whatever size the caller renders at, and at any device pixel ratio. The site
 * draws these at both 17x13 and 16x12; neither number appears here.
 *
 * `background-position: P%` then aligns P% of the (scaled) image with P% of the
 * element, i.e. it offsets by `P × (imageWidth − elementWidth)`. Setting
 * `P = x / (sheetW − cellW)` therefore lands cell x at the element's left edge.
 * That formula holds with the padding in place, which the naive
 * `col / (cols − 1)` version does not.
 */
function cellStyle(index: number): CSSProperties {
  const tileW = SHEET.cellW + SHEET.pad * 2;
  const tileH = SHEET.cellH + SHEET.pad * 2;
  const x = (index % SHEET.cols) * tileW + SHEET.pad;
  const y = Math.floor(index / SHEET.cols) * tileH + SHEET.pad;

  return {
    backgroundImage: `url(${sheet.src})`,
    backgroundSize: `${(SHEET_W / SHEET.cellW) * 100}% ${(SHEET_H / SHEET.cellH) * 100}%`,
    backgroundPosition: `${(x / (SHEET_W - SHEET.cellW)) * 100}% ${(y / (SHEET_H - SHEET.cellH)) * 100}%`,
    backgroundRepeat: 'no-repeat',
  };
}

/** Whether the sprite actually has artwork for a code. */
export function hasFlag(code: string | null | undefined): boolean {
  return !!code && FLAG_INDEX[code.toLowerCase()] !== undefined;
}

/**
 * Whether a flag should be DRAWN — artwork exists AND this person has not
 * switched it off in Settings ▸ Flags.
 *
 * ── Why a hook, when `hasFlag` is a plain function ──────────────────────────
 * Because the answer now depends on who is looking, and it changes while they
 * look: flipping a switch in the dialog has to empty the slot behind it without
 * a reload. Callers that only need "does this exist" keep using `hasFlag`; the
 * ones that DRAW use this, and there are only four of them.
 *
 * ── Why it is a separate question from `hasFlag` ────────────────────────────
 * Because the two have different fallbacks, and the difference is visible. A
 * code with no artwork is a gap in our sprite and the slot is simply left
 * empty. A code somebody has switched OFF is a choice, and the one place that
 * cannot leave a hole — the Registers button, which is a button and needs a
 * face — draws the site's own mark instead. That is not a new behaviour: it is
 * exactly what Korean did back when its flag was missing from the registry
 * rather than switched off, and keeping it is what makes this setting invisible
 * to everybody who was already using the product.
 *
 * OUTSIDE AN AuthProvider (the admin console) nothing is hidden, because nobody
 * there has expressed a preference — see the FALLBACK in lib/auth.
 */
export function useFlagVisible(): (code: string | null | undefined) => boolean {
  const { flagsOff } = useAuth();
  // A Set, and memoised on the array identity: the language picker asks this
  // question ~250 times per open, and `Array.includes` over a list somebody has
  // hidden forty flags in is forty comparisons each time.
  const off = useMemo(() => new Set(flagsOff), [flagsOff]);
  return useCallback(
    (code) => {
      if (!code) return false;
      const lower = code.toLowerCase();
      return FLAG_INDEX[lower] !== undefined && !off.has(lower);
    },
    [off],
  );
}

/**
 * Renders `null` for a code the sheet does not contain — a geo database knows
 * more country codes than any flag set does. Callers decide what to show
 * instead, because the answer differs: the admin console draws a globe, the
 * language picker simply leaves the slot empty.
 */
export default function FlagIcon({
  code,
  title,
  className = '',
  style,
}: {
  code: string | null | undefined;
  title?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const index = code ? FLAG_INDEX[code.toLowerCase()] : undefined;
  if (index === undefined) return null;

  return (
    <span
      aria-hidden
      title={title}
      className={`inline-block shrink-0 ${className}`}
      style={{ ...cellStyle(index), ...style }}
    />
  );
}
